import { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Send,
  Image as ImageIcon,
  Check,
  CheckCheck,
  Loader2,
  X,
  MoreVertical,
  Pencil,
  Trash2,
} from 'lucide-react';
import {
  useGetConversationQuery,
  useGetMessagesQuery,
  useSendMessageMutation,
  useMarkAsReadMutation,
  useGetPresignedUrlMutation,
  useEditMessageMutation,
  useDeleteMessageMutation,
  useAppSelector,
  selectCurrentUser,
} from '@nimbazaar/store';
import { useToast } from '../Toast';
import { EmojiReactions } from '../EmojiReactions';
import type { MessageWithSender } from '@nimbazaar/types';
import type { Socket } from 'socket.io-client';

interface OptimisticMessage extends MessageWithSender {
  _optimistic: true;
  _localImageUrl?: string; // blob URL for instant image preview
  _uploading?: boolean;
  _failed?: boolean;
}

interface ChatViewProps {
  conversationId: string;
  socket: Socket | null;
  isConnected: boolean;
  onlineUsers: Set<string>;
  typingUsers: Map<string, string>;
  onBack: () => void;
}

export function ChatView({
  conversationId,
  socket,
  isConnected,
  onlineUsers,
  typingUsers,
  onBack,
}: ChatViewProps) {
  const currentUser = useAppSelector(selectCurrentUser);
  const { data: convData, isError: convError } = useGetConversationQuery(conversationId);
  const { data: messagesData, isLoading: messagesLoading, isError: msgsError } = useGetMessagesQuery({
    conversationId,
    limit: 100,
  });
  const [sendMessage] = useSendMessageMutation();
  const [markAsRead] = useMarkAsReadMutation();
  const [getPresignedUrl] = useGetPresignedUrlMutation();
  const toast = useToast();

  const [input, setInput] = useState('');
  const [imagePreview, setImagePreview] = useState<{ file: File; url: string } | null>(null);
  const [optimisticMessages, setOptimisticMessages] = useState<OptimisticMessage[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // P2 #21: Guard against double-send
  const sendingRef = useRef(false);

  const conversation = convData?.data;
  const serverMessages = messagesData?.data ?? [];
  const otherUser = conversation?.otherUser;
  const isOtherOnline = otherUser ? onlineUsers.has(otherUser.id) : false;
  const isOtherTyping = typingUsers.has(conversationId);

  // Merge server messages with optimistic messages, removing confirmed ones
  const messages = useMemo(() => {
    const remaining = [...optimisticMessages.filter((om) => om.conversationId === conversationId)];
    const pending: OptimisticMessage[] = [];

    for (const om of remaining) {
      const matched = serverMessages.some(
        (sm) =>
          sm.senderId === om.senderId &&
          sm.content === om.content &&
          sm.type === om.type &&
          Math.abs(new Date(sm.createdAt).getTime() - new Date(om.createdAt).getTime()) < 30000
      );
      if (!matched) {
        pending.push(om);
      }
    }
    return [...serverMessages, ...pending];
  }, [serverMessages, optimisticMessages, conversationId]);

  // P0 #5: Clean up confirmed optimistic messages from state + revoke blob URLs
  useEffect(() => {
    if (serverMessages.length === 0 || optimisticMessages.length === 0) return;
    setOptimisticMessages((prev) => {
      const filtered = prev.filter((om) => {
        if (om.conversationId !== conversationId) return true;
        const matched = serverMessages.some(
          (sm) =>
            sm.senderId === om.senderId &&
            sm.content === om.content &&
            sm.type === om.type &&
            Math.abs(new Date(sm.createdAt).getTime() - new Date(om.createdAt).getTime()) < 30000
        );
        if (matched && om._localImageUrl) {
          URL.revokeObjectURL(om._localImageUrl);
        }
        return !matched;
      });
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [serverMessages, conversationId, optimisticMessages.length]);

  // Clear optimistic messages when switching conversations
  useEffect(() => {
    return () => {
      // Revoke any remaining blob URLs on unmount/conversation switch
      setOptimisticMessages((prev) => {
        for (const om of prev) {
          if (om._localImageUrl) URL.revokeObjectURL(om._localImageUrl);
        }
        return [];
      });
    };
  }, [conversationId]);

  // P3 #28: Clean up typing timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, []);

  // Join conversation room and refresh online status
  useEffect(() => {
    if (socket && conversationId) {
      socket.emit('join_conversation', conversationId);
      socket.emit('get_online_status');
    }
  }, [socket, conversationId]);

  // P2 #22: Mark as read only when latest message is from the other user
  useEffect(() => {
    if (!conversationId || serverMessages.length === 0) return;
    const lastMsg = serverMessages[serverMessages.length - 1];
    if (lastMsg && lastMsg.senderId !== currentUser?.id && !lastMsg.isRead) {
      markAsRead(conversationId);
    }
  }, [conversationId, serverMessages, currentUser?.id, markAsRead]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, isOtherTyping]);

  // Focus input
  useEffect(() => {
    inputRef.current?.focus();
  }, [conversationId]);

  const addOptimisticMessage = useCallback(
    (content: string, type: 'TEXT' | 'IMAGE', localImageUrl?: string): string => {
      const tempId = `optimistic-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const msg: OptimisticMessage = {
        id: tempId,
        conversationId,
        senderId: currentUser?.id ?? '',
        content,
        type,
        imageUrl: localImageUrl ?? null,
        isRead: false,
        isEdited: false,
        isDeleted: false,
        readAt: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        sender: {
          id: currentUser?.id ?? '',
          name: currentUser?.name ?? '',
          avatar: currentUser?.avatar ?? undefined,
        },
        _optimistic: true,
        _localImageUrl: localImageUrl,
        _uploading: type === 'IMAGE',
      };
      setOptimisticMessages((prev) => [...prev, msg]);
      return tempId;
    },
    [conversationId, currentUser]
  );

  const handleSend = useCallback(async () => {
    // P2 #21: Prevent double-send
    if (sendingRef.current) return;
    sendingRef.current = true;

    try {
      const text = input.trim();

      if (imagePreview) {
        const localUrl = imagePreview.url;
        const file = imagePreview.file;
        const msgContent = text || '📷 Photo';

        const tempId = addOptimisticMessage(msgContent, 'IMAGE', localUrl);

        setImagePreview(null);
        setInput('');

        try {
          const presigned = await getPresignedUrl({
            filename: file.name,
            contentType: file.type,
            folder: 'chat',
          }).unwrap();

          await fetch(presigned.data.uploadUrl, {
            method: 'PUT',
            body: file,
            headers: { 'Content-Type': file.type },
          });

          if (socket?.connected) {
            socket.emit('send_message', {
              conversationId,
              content: msgContent,
              type: 'IMAGE',
              imageUrl: presigned.data.publicUrl,
            });
          } else {
            await sendMessage({
              conversationId,
              content: msgContent,
              type: 'IMAGE',
              imageUrl: presigned.data.publicUrl,
            });
          }
        } catch (err) {
          console.error('Upload failed:', err);
          setOptimisticMessages((prev) =>
            prev.map((m) => (m.id === tempId ? { ...m, _failed: true, _uploading: false } : m))
          );
        }
        return;
      }

      if (!text) return;

      const tempId = addOptimisticMessage(text, 'TEXT');
      setInput('');

      // P2 #24: Add error handling for text send
      try {
        if (socket?.connected) {
          socket.emit('send_message', {
            conversationId,
            content: text,
            type: 'TEXT',
          });
          socket.emit('stop_typing', conversationId);
        } else {
          await sendMessage({ conversationId, content: text });
        }
      } catch (err) {
        console.error('Send failed:', err);
        setOptimisticMessages((prev) =>
          prev.map((m) => (m.id === tempId ? { ...m, _failed: true } : m))
        );
      }
    } finally {
      sendingRef.current = false;
    }
  }, [input, imagePreview, socket, conversationId, sendMessage, getPresignedUrl, addOptimisticMessage]);

  const handleInputChange = useCallback((value: string) => {
    setInput(value);

    if (socket?.connected) {
      socket.emit('typing', conversationId);

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      typingTimeoutRef.current = setTimeout(() => {
        socket.emit('stop_typing', conversationId);
      }, 2000);
    }
  }, [socket, conversationId]);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Invalid File', 'Only image files are allowed');
      e.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File Too Large', 'File too large. Max size is 5MB');
      e.target.value = '';
      return;
    }

    setImagePreview({
      file,
      url: URL.createObjectURL(file),
    });
    e.target.value = '';
  };

  // P3 #29: Memoize grouped messages
  const groupedMessages = useMemo(() => groupMessagesByDate(messages), [messages]);

  return (
    <div className="flex-1 flex flex-col h-full">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
        <button
          onClick={onBack}
          aria-label="Go back"
          className="md:hidden p-1.5 hover:bg-gray-100 rounded-full transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>

        {otherUser && (
          <>
            <div className="relative">
              {otherUser.avatar ? (
                <img
                  src={otherUser.avatar}
                  alt={otherUser.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-sm font-semibold text-primary-600">
                    {otherUser.name.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              {isOtherOnline && (
                <span
                  className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"
                  aria-label="Online"
                />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900">{otherUser.name}</p>
              <p className="text-xs text-gray-400" role="status" aria-live="polite">
                {isOtherTyping
                  ? 'Typing...'
                  : isOtherOnline
                    ? 'Online'
                    : 'Offline'}
              </p>
            </div>
          </>
        )}

        {conversation?.ad && (
          <Link
            to={`/ad/${conversation.ad.slug}`}
            className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors shrink-0"
          >
            {conversation.ad.images?.[0] && (
              <img
                src={conversation.ad.images[0].url}
                alt=""
                className="w-7 h-7 rounded object-cover"
              />
            )}
            <span className="text-xs text-gray-600 max-w-[120px] truncate">
              {conversation.ad.title}
            </span>
          </Link>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {messagesLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-primary-500 animate-spin" />
          </div>
        ) : (convError || msgsError) ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-sm text-red-400">Failed to load messages. Please try again.</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-sm text-gray-400">Send a message to start the conversation</p>
          </div>
        ) : (
          groupedMessages.map(({ date, messages: msgs }) => (
            <div key={date}>
              <div className="flex items-center justify-center my-4">
                <span className="text-[11px] text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
                  {date}
                </span>
              </div>
              {msgs.map((msg) => (
                <MessageBubble
                  key={msg.id}
                  message={msg}
                  isMine={msg.senderId === currentUser?.id}
                  currentUserId={currentUser?.id}
                  isOptimistic={'_optimistic' in msg}
                  isUploading={'_uploading' in msg && (msg as OptimisticMessage)._uploading}
                  isFailed={'_failed' in msg && (msg as OptimisticMessage)._failed}
                  localImageUrl={'_localImageUrl' in msg ? (msg as OptimisticMessage)._localImageUrl : undefined}
                  socket={socket}
                  conversationId={conversationId}
                  isConnected={isConnected}
                />
              ))}
            </div>
          ))
        )}

        {/* Typing indicator */}
        {isOtherTyping && (
          <div className="flex items-center gap-2 mb-2" role="status" aria-live="polite">
            <div className="bg-gray-100 rounded-2xl rounded-bl-md px-4 py-2.5">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Image Preview */}
      {imagePreview && (
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
          <div className="relative inline-block">
            <img
              src={imagePreview.url}
              alt="Preview"
              className="h-20 w-20 object-cover rounded-lg border border-gray-200"
            />
            <button
              onClick={() => {
                URL.revokeObjectURL(imagePreview.url);
                setImagePreview(null);
              }}
              aria-label="Remove image"
              className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="bg-white border-t border-gray-200 px-3 sm:px-4 py-2.5 sm:py-3 safe-bottom">
        <div className="flex items-center gap-2">
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            className="hidden"
            onChange={handleImageSelect}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            aria-label="Attach image"
            className="p-2 text-gray-400 hover:text-primary-500 hover:bg-primary-50 rounded-full transition-colors"
          >
            <ImageIcon className="w-5 h-5" />
          </button>

          <input
            ref={inputRef}
            type="text"
            placeholder="Type a message..."
            value={input}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            className="flex-1 px-4 py-2.5 border border-gray-200 rounded-full bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />

          <button
            onClick={handleSend}
            disabled={!input.trim() && !imagePreview}
            aria-label="Send message"
            className="p-2.5 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-200 disabled:text-gray-400 text-white rounded-full transition-colors"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>

        {!isConnected && (
          <p className="text-[11px] text-amber-500 mt-1 text-center">
            Reconnecting...
          </p>
        )}
      </div>
    </div>
  );
}

// --- Message Bubble (P1 #10: wrapped in React.memo) ---

interface MessageBubbleProps {
  message: MessageWithSender;
  isMine: boolean;
  currentUserId?: string;
  isOptimistic?: boolean;
  isUploading?: boolean;
  isFailed?: boolean;
  localImageUrl?: string;
  socket: Socket | null;
  conversationId: string;
  isConnected: boolean;
}

const MessageBubble = memo(function MessageBubble({
  message,
  isMine,
  currentUserId,
  isOptimistic,
  isUploading,
  isFailed,
  localImageUrl,
  socket,
  conversationId,
  isConnected,
}: MessageBubbleProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(message.content);
  const [editImagePreview, setEditImagePreview] = useState<{ file: File; url: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [optimisticEmoji, setOptimisticEmoji] = useState<string | null>(null);
  const [getPresignedUrl] = useGetPresignedUrlMutation();
  const [editMessage] = useEditMessageMutation();
  const [deleteMessage] = useDeleteMessageMutation();
  const menuRef = useRef<HTMLDivElement>(null);
  const editFileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  // Sync optimistic emoji with server data
  const serverEmoji = (message.reactions?.length ?? 0) > 0
    ? message.reactions![message.reactions!.length - 1].emoji
    : null;

  useEffect(() => {
    if (serverEmoji !== null) setOptimisticEmoji(null);
  }, [serverEmoji]);

  const displayEmoji = optimisticEmoji ?? serverEmoji;

  // P2 #14: Proper type access (no `as any` — fields are on MessageWithSender now)
  const isDeleted = message.isDeleted;
  const isEdited = message.isEdited;

  // P3 #29: Memoize formatted time
  const time = useMemo(
    () =>
      new Date(message.createdAt).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      }),
    [message.createdAt]
  );

  // Close menu on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
        setConfirmDelete(false);
      }
    };
    if (showMenu || confirmDelete) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [showMenu, confirmDelete]);

  // P3 #34: Clean up editImagePreview blob URL on unmount
  useEffect(() => {
    return () => {
      if (editImagePreview) URL.revokeObjectURL(editImagePreview.url);
    };
  }, [editImagePreview]);

  const handleEdit = () => {
    setShowMenu(false);
    setEditContent(message.content);
    setEditImagePreview(null);
    setIsEditing(true);
  };

  const handleEditSave = async () => {
    const text = editContent.trim();
    if (!text && !editImagePreview) return;

    if (editImagePreview) {
      try {
        const presigned = await getPresignedUrl({
          filename: editImagePreview.file.name,
          contentType: editImagePreview.file.type,
          folder: 'chat',
        }).unwrap();

        await fetch(presigned.data.uploadUrl, {
          method: 'PUT',
          body: editImagePreview.file,
          headers: { 'Content-Type': editImagePreview.file.type },
        });

        // P2 #15: Use socket with REST fallback
        if (socket?.connected) {
          socket.emit('edit_message', {
            conversationId,
            messageId: message.id,
            content: text || message.content,
            type: 'IMAGE',
            imageUrl: presigned.data.publicUrl,
          });
        } else {
          await editMessage({
            conversationId,
            messageId: message.id,
            content: text || message.content,
            type: 'IMAGE',
            imageUrl: presigned.data.publicUrl,
          });
        }
      } catch {
        toast.error('Upload Failed', 'Failed to upload image');
        return; // P3 #36: Keep edit UI open on failure
      }
    } else {
      // P2 #15: Use socket with REST fallback
      if (socket?.connected) {
        socket.emit('edit_message', {
          conversationId,
          messageId: message.id,
          content: text,
        });
      } else {
        try {
          await editMessage({
            conversationId,
            messageId: message.id,
            content: text,
          });
        } catch {
          toast.error('Edit Failed', 'Failed to edit message');
          return;
        }
      }
    }

    setIsEditing(false);
    setEditImagePreview(null);
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setEditContent(message.content);
    if (editImagePreview) {
      URL.revokeObjectURL(editImagePreview.url);
      setEditImagePreview(null);
    }
  };

  const handleDelete = () => {
    // P2 #15: Use socket with REST fallback
    if (socket?.connected) {
      socket.emit('delete_message', {
        conversationId,
        messageId: message.id,
      });
    } else {
      deleteMessage({ conversationId, messageId: message.id }).catch(() => {
        toast.error('Delete Failed', 'Failed to delete message');
      });
    }
    setConfirmDelete(false);
    setShowMenu(false);
  };

  const handleEditImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Invalid File', 'Only image files are allowed');
      e.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File Too Large', 'File too large. Max size is 5MB');
      e.target.value = '';
      return;
    }
    if (editImagePreview) URL.revokeObjectURL(editImagePreview.url);
    setEditImagePreview({ file, url: URL.createObjectURL(file) });
    e.target.value = '';
  };

  const displayImageUrl = localImageUrl || message.imageUrl;

  // Deleted message
  if (isDeleted) {
    return (
      <div className={`flex mb-2 ${isMine ? 'justify-end' : 'justify-start'}`}>
        <div
          className={`max-w-[75%] px-4 py-2.5 ${
            isMine
              ? 'bg-primary-200/50 rounded-2xl rounded-br-md'
              : 'bg-gray-50 rounded-2xl rounded-bl-md'
          }`}
        >
          <p className="text-sm italic text-gray-400">This message was deleted</p>
          <div className={`flex items-center gap-1 mt-1 ${isMine ? 'justify-end' : ''}`}>
            <span className="text-[10px] text-gray-400">{time}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`group flex mb-2 ${isMine ? 'justify-end' : 'justify-start'}`}>
      {/* Action menu (mine only, non-optimistic) */}
      {isMine && !isOptimistic && (
        <div className="relative flex items-center mr-1 opacity-0 group-hover:opacity-100 transition-opacity" ref={menuRef}>
          {confirmDelete ? (
            <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg shadow-sm px-2 py-1">
              <span className="text-xs text-gray-500">Delete?</span>
              <button
                onClick={handleDelete}
                className="text-xs text-red-500 hover:text-red-700 font-medium px-1"
              >
                Yes
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="text-xs text-gray-400 hover:text-gray-600 font-medium px-1"
              >
                No
              </button>
            </div>
          ) : showMenu ? (
            <div className="flex items-center gap-0.5 bg-white border border-gray-200 rounded-lg shadow-sm px-1 py-0.5">
              <button
                onClick={handleEdit}
                className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
                title="Edit"
                aria-label="Edit message"
              >
                <Pencil className="w-3.5 h-3.5 text-gray-500" />
              </button>
              <button
                onClick={() => { setShowMenu(false); setConfirmDelete(true); }}
                className="p-1.5 hover:bg-red-50 rounded-md transition-colors"
                title="Delete"
                aria-label="Delete message"
              >
                <Trash2 className="w-3.5 h-3.5 text-red-400" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowMenu(true)}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Message actions"
            >
              <MoreVertical className="w-4 h-4 text-gray-400" />
            </button>
          )}
        </div>
      )}

      <div className="flex flex-col max-w-[75%]">
      <div
        className={`relative ${
          isMine
            ? 'bg-primary-500 text-white rounded-2xl rounded-br-md'
            : 'bg-gray-100 text-gray-900 rounded-2xl rounded-bl-md'
        } ${isFailed ? 'opacity-60' : ''} px-4 py-2.5 ${!isDeleted && displayEmoji ? 'mb-3' : ''}`}
      >
        {isEditing ? (
          <div className="min-w-[200px]">
            <input
              type="file"
              ref={editFileInputRef}
              accept="image/*"
              className="hidden"
              onChange={handleEditImageSelect}
            />
            {message.type === 'IMAGE' && (
              <div className="mb-2">
                {editImagePreview ? (
                  <div className="relative inline-block">
                    <img src={editImagePreview.url} alt="New" className="max-w-full rounded-lg" style={{ maxHeight: '160px' }} />
                    <button
                      onClick={() => { URL.revokeObjectURL(editImagePreview.url); setEditImagePreview(null); }}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                      aria-label="Remove replacement image"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : displayImageUrl ? (
                    <div className="relative inline-block">
                      <img src={displayImageUrl} alt="Current" className="max-w-full rounded-lg opacity-70" style={{ maxHeight: '160px' }} />
                    </div>
                ) : null}
                <button
                  onClick={() => editFileInputRef.current?.click()}
                  className="mt-1 text-xs text-white/80 hover:text-white underline"
                >
                  Replace image
                </button>
              </div>
            )}
            <input
              type="text"
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleEditSave(); if (e.key === 'Escape') handleEditCancel(); }}
              className="w-full px-2 py-1 text-sm rounded bg-white/20 text-white placeholder-white/50 border border-white/30 focus:outline-none focus:ring-1 focus:ring-white/50"
              autoFocus
            />
            <div className="flex gap-2 mt-1.5 justify-end">
              <button onClick={handleEditCancel} className="text-[11px] text-white/60 hover:text-white">Cancel</button>
              <button onClick={handleEditSave} className="text-[11px] font-medium text-white hover:text-white/90 bg-white/20 px-2 py-0.5 rounded">Save</button>
            </div>
          </div>
        ) : (
          <>
            {message.type === 'IMAGE' && displayImageUrl && (
              <div className="relative">
                {!isOptimistic ? (
                  <a href={displayImageUrl} target="_blank" rel="noopener noreferrer">
                    <img
                      src={displayImageUrl}
                      alt="Shared image"
                      className="max-w-full rounded-lg mb-1.5"
                      style={{ maxHeight: '240px' }}
                    />
                  </a>
                ) : (
                  <img
                    src={displayImageUrl}
                    alt="Shared image"
                    className="max-w-full rounded-lg mb-1.5"
                    style={{ maxHeight: '240px' }}
                  />
                )}
                {isUploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-lg">
                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                  </div>
                )}
              </div>
            )}

            {(message.type === 'TEXT' || (message.type === 'IMAGE' && message.content && message.content !== '📷 Photo')) && (
              <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
            )}

            <div className={`flex items-center gap-1 mt-1 ${isMine ? 'justify-end' : ''}`}>
              {isEdited && (
                <span className={`text-[10px] ${isMine ? 'text-white/50' : 'text-gray-400'}`}>
                  (edited)
                </span>
              )}
              <span
                className={`text-[10px] ${isMine ? 'text-white/70' : 'text-gray-400'}`}
              >
                {time}
              </span>
              {isMine && (
                isFailed ? (
                  <span className="text-[10px] text-red-200">Failed</span>
                ) : isOptimistic ? (
                  <Loader2 className="w-3 h-3 text-white/50 animate-spin" />
                ) : message.isRead ? (
                  <CheckCheck className="w-3.5 h-3.5 text-blue-200" />
                ) : (
                  <Check className="w-3.5 h-3.5 text-white/50" />
                )
              )}
            </div>

          </>
        )}

        {/* Emoji reaction badge — bottom-left of bubble */}
        {!isDeleted && displayEmoji && (
          <span
            className={`absolute -bottom-2.5 ${isMine ? 'left-2' : 'left-2'} bg-white rounded-full px-1 py-0.5 shadow-sm border border-gray-100 text-sm leading-none cursor-pointer hover:scale-110 transition-transform`}
            onClick={() => {
              setOptimisticEmoji(null);
              if (socket?.connected) {
                socket.emit('react_message', { messageId: message.id, emoji: displayEmoji, conversationId });
              }
            }}
            title="Click to remove"
          >
            {displayEmoji}
          </span>
        )}
      </div>

      {/* Reaction picker button — below bubble */}
      {!isDeleted && (
        <div className={`${isMine ? 'flex justify-end' : ''}`}>
          <EmojiReactions
            reactions={[]}
            currentUserId={currentUserId}
            singleMode
            onReact={(emoji) => {
              // Optimistic: show immediately
              setOptimisticEmoji(emoji);
              if (socket?.connected) {
                socket.emit('react_message', { messageId: message.id, emoji, conversationId });
              }
            }}
          />
        </div>
      )}
      </div>
    </div>
  );
});

// --- Helpers ---

function groupMessagesByDate(messages: (MessageWithSender | OptimisticMessage)[]) {
  const groups: { date: string; messages: (MessageWithSender | OptimisticMessage)[] }[] = [];
  let currentDate = '';

  for (const msg of messages) {
    const d = new Date(msg.createdAt);
    const dateStr = formatDateLabel(d);

    if (dateStr !== currentDate) {
      currentDate = dateStr;
      groups.push({ date: dateStr, messages: [msg] });
    } else {
      groups[groups.length - 1].messages.push(msg);
    }
  }

  return groups;
}

function formatDateLabel(date: Date) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.floor((today.getTime() - target.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return date.toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' });
}
